import { useState, useRef, useEffect, useCallback, useDeferredValue, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useT } from '../../i18n/useT'

const MAX_VISIBLE = 40
/** Tallest the list gets before it scrolls inside itself */
const MENU_MAX_HEIGHT = 240
/** Smallest list worth opening downward rather than flipping up */
const MENU_MIN_HEIGHT = 120
const GAP = 4

interface SelectOption {
  value: string
  label: string
}

interface SelectProps {
  options: SelectOption[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  searchable?: boolean
  label?: string
  error?: boolean
}

interface MenuPosition {
  left: number
  top: number
  width: number
  maxHeight: number
}

/** Where a sticky header ends, so an upward-opening list doesn't slide under
 * it. Read from the page rather than hard-coded: the header is the one
 * element allowed to sit above this menu, and its height is its own business. */
function stickyHeaderBottom(): number {
  const el = document.querySelector('header')
  if (!el) return 0
  const position = getComputedStyle(el).position
  if (position !== 'sticky' && position !== 'fixed') return 0
  return Math.max(0, el.getBoundingClientRect().bottom)
}

/** The list is placed against the viewport, not against the page.
 *
 * Three faults came from placing it in flow: it drew over the sticky header
 * (z-40 against the header's z-30), the card's `overflow-hidden` cut it off,
 * and it always opened downward — measured 42 px past the bottom of the
 * window for a picker near the fold, on a page with nothing left to scroll. */
function measureMenu(button: HTMLElement): MenuPosition {
  const rect = button.getBoundingClientRect()
  const roomBelow = window.innerHeight - rect.bottom - GAP
  const roomAbove = rect.top - GAP - stickyHeaderBottom()
  const openUpward = roomBelow < MENU_MIN_HEIGHT && roomAbove > roomBelow
  const room = openUpward ? roomAbove : roomBelow
  const maxHeight = Math.max(MENU_MIN_HEIGHT, Math.min(MENU_MAX_HEIGHT, room))
  return {
    left: rect.left,
    width: rect.width,
    top: openUpward ? rect.top - GAP - maxHeight : rect.bottom + GAP,
    maxHeight,
  }
}

export function Select({
  options,
  value,
  onChange,
  placeholder,
  searchable = false,
  label,
  error,
}: SelectProps) {
  const t = useT()
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [position, setPosition] = useState<MenuPosition | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const selectedOption = options.find((o) => o.value === value)

  // Typing stays instant: the list re-filters against a deferred copy of the
  // query, so React renders the keystroke first and the 95-item list in a
  // background render that the next keystroke can interrupt
  const deferredSearch = useDeferredValue(search)
  const filtered = useMemo(() => {
    if (!searchable || !deferredSearch) return options
    const q = deferredSearch.toLowerCase()
    return options.filter((o) => o.label.toLowerCase().includes(q))
  }, [options, deferredSearch, searchable])
  // One tap used to mount 95 buttons at once — the single biggest INP hit on
  // phones. Search is always there, so the cap costs nothing in reach.
  const visible = useMemo(() => filtered.slice(0, MAX_VISIBLE), [filtered])

  const reposition = useCallback(() => {
    if (buttonRef.current) setPosition(measureMenu(buttonRef.current))
  }, [])

  function close() {
    setIsOpen(false)
    setSearch('')
  }

  function toggle() {
    if (isOpen) {
      close()
      return
    }
    reposition()
    setIsOpen(true)
  }

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node
      // The menu lives in a portal now, so "outside" is both boxes
      if (containerRef.current?.contains(target) || menuRef.current?.contains(target)) return
      close()
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!isOpen) return
    // Capture: the scroller may be any ancestor, not just the window
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [isOpen, reposition])

  useEffect(() => {
    if (isOpen && searchable && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen, searchable])

  const menu = isOpen && position && (
    <div
      ref={menuRef}
      // z-20 keeps it under the sticky header (z-30), which is the one thing
      // that should stay legible while a list is open
      className="fixed z-20 bg-surface border border-border rounded-lg shadow-lg overflow-hidden"
      style={{
        left: position.left,
        top: position.top,
        width: position.width,
        maxHeight: position.maxHeight,
      }}
    >
      {searchable && (
        <div className="p-2 border-b border-border">
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('select.search')}
            className="w-full px-2.5 py-1.5 text-sm border border-border rounded-md bg-surface focus:outline-none focus:border-border-focus"
          />
        </div>
      )}
      <div className="overflow-y-auto" style={{ maxHeight: position.maxHeight - (searchable ? 48 : 0) }}>
        {filtered.length === 0 ? (
          <div className="px-3 py-2 text-sm text-text-muted">{t('select.noResults')}</div>
        ) : (
          visible.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value)
                close()
              }}
              className={`w-full text-left px-3 py-2 text-sm transition-colors hover:bg-surface-hover ${
                option.value === value ? 'bg-primary/5 text-primary font-medium' : 'text-text'
              }`}
            >
              {option.label}
            </button>
          ))
        )}
        {filtered.length > MAX_VISIBLE && (
          <div className="px-3 py-2 text-xs text-text-muted border-t border-border">
            {t('select.refineSearch', { count: MAX_VISIBLE })}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className="block text-sm font-medium text-text-secondary mb-1.5">{label}</label>
      )}
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border text-left text-sm transition-colors ${
          error
            ? 'border-error focus:ring-error'
            : 'border-border hover:border-text-muted focus:border-border-focus focus:ring-1 focus:ring-border-focus'
        } bg-surface`}
      >
        <span className={selectedOption ? 'text-text' : 'text-text-muted'}>
          {selectedOption?.label || placeholder || t('select.placeholder')}
        </span>
        <svg
          className={`w-4 h-4 text-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {menu && createPortal(menu, document.body)}
    </div>
  )
}
